import { yupResolver } from '@hookform/resolvers/yup';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneIcon from '@mui/icons-material/Done';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { FC, useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import * as yup from 'yup';

import { SubmitReferenceData } from '@features/applicant';
import { usePostApplicantReference } from '@features/applicant/hooks/usePostApplicantReference';
import { Close } from '@mui/icons-material';
import { getStrippedText } from '@utils/helper';

type AddReferenceModalProps = {
  isOpen: boolean;
  handleClose: () => void;
};

const schema: yup.ObjectSchema<SubmitReferenceData> = yup
  .object({
    name: yup
      .string()
      .required('Name is required')
      .test('not-empty-html', 'Name cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    company: yup
      .string()
      .required('Company is required')
      .test('not-empty-html', 'Company cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    position: yup
      .string()
      .required('Position is required')
      .test('not-empty-html', 'Position cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    email: yup
      .string()
      .required('Email is required')
      .test('is-email', 'Must be a valid email', (value: string) =>
        isEmail(value),
      ),
    phone: yup
      .string()
      .required('Phone is required')
      .test('is-phone', 'Must be a valid phone', (value: string) =>
        isMobilePhone(value),
      ),
  })
  .required();

const AddReferenceModal: FC<AddReferenceModalProps> = ({
  isOpen,
  handleClose,
}) => {
  const [errorText, setErrorText] = useState('');

  const {
    handleSubmit,
    control,
    formState: { errors, touchedFields },
    reset,
  } = useForm<SubmitReferenceData>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      company: '',
      position: '',
      email: '',
      phone: '',
    },
  });

  const {
    authData: { user },
  } = useContext(authContext);

  const { postApplicantReference, isPostApplicantReferenceLoading } =
    usePostApplicantReference({
      setErrorText,
      applicantId: user?.id!,
    });

  const onSubmit = (values: SubmitReferenceData) => {
    postApplicantReference(values, {
      onSuccess: (data) => {
        if (data.status === 200) {
          toast.success('Reference requested');
          handleClose();
          reset();
        }
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        handleClose();
        reset();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <Typography variant="h4" m={2}>
          Add Reference
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="addApplicantForm"
          >
            <Stack
              spacing={3}
              sx={{
                pt: 1,
                '& p': {
                  ml: 1,
                  fontSize: '15px',
                },
              }}
            >
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.name)}
                    helperText={errors?.name ? errors?.name.message : ''}
                    InputProps={{
                      endAdornment: (
                        <>
                          {errors.name && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.name && !errors.name && (
                            <CheckCircleIcon
                              color="success"
                              fontSize="medium"
                            />
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="company"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Company"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.company)}
                    helperText={errors?.company ? errors?.company.message : ''}
                    InputProps={{
                      endAdornment: (
                        <>
                          {errors.company && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.company && !errors.company && (
                            <CheckCircleIcon
                              color="success"
                              fontSize="medium"
                            />
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="position"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Position"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.position)}
                    helperText={
                      errors?.position ? errors?.position.message : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <>
                          {errors.position && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.position && !errors.position && (
                            <CheckCircleIcon
                              color="success"
                              fontSize="medium"
                            />
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.phone)}
                    helperText={errors?.phone ? errors?.phone.message : ''}
                    InputProps={{
                      endAdornment: (
                        <>
                          {errors.phone && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.phone && !errors.phone && (
                            <CheckCircleIcon
                              color="success"
                              fontSize="medium"
                            />
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors?.email ? errors?.email.message : ''}
                    InputProps={{
                      endAdornment: (
                        <>
                          {errors.email && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.email && !errors.email && (
                            <CheckCircleIcon
                              color="success"
                              fontSize="medium"
                            />
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <LoadingButton
                type="submit"
                startIcon={<DoneIcon />}
                variant="contained"
                size="small"
                fullWidth
                loading={isPostApplicantReferenceLoading}
                loadingPosition="start"
                sx={{ margin: '20px 0 10px 0 !important' }}
              >
                Add Reference
              </LoadingButton>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AddReferenceModal;
